import type { ResellerPermission } from "@xop-any-time/shared";

import { useAuth } from "../../../auth/AuthProvider.js";

/**
 * Who may add and remove a file, on which detail page (spec §3.3).
 *
 * IT LIVES HERE, OUTSIDE `src/crm`, for the reason
 * `pages/portal/priceList.ts` gives for the price-list ruling: the ported
 * tree is excluded from this repo's type program, so a rule written in there
 * would be neither typechecked nor comfortably testable. The ported panel
 * CALLS this; the reasoning and the tests are here.
 *
 * RENDERING ONLY. crm-api decides — `CustomerAttachmentsController` requires
 * `customers:edit` to upload or destroy and refuses a blob this reseller did
 * not upload; `LeadAttachmentsController` requires `prospects:view` (spec
 * §4.4). Hiding a button here is a courtesy; showing one can still be
 * refused.
 */

export type AttachmentScope = "customer" | "prospect";

/**
 * The key each surface's WRITE side needs.
 *
 * The asymmetry is deliberate and is the ruling itself. A customer's files
 * follow the customer's own edit key. A prospect's follow `prospects:view`,
 * because the files are the reseller's own documents ABOUT the lead rather
 * than the lead's data.
 *
 * The ruling was written while `prospects:edit` was still ungrantable in the
 * catalog, which made `prospects:view` the only expressible answer. Ruling P3
 * (spec 2026-09-09 §4.5) has since made `prospects:edit` grantable — it now
 * gates the lead's OWN fields (create/update/destroy/convert). The files
 * deliberately stayed on the view key: a member who may only READ a prospect
 * can still keep its company's paperwork on one. Do not "tidy" this onto
 * `prospects:edit`; crm-api's `LeadAttachmentsController` gates all five
 * actions on `prospects:view` and would then refuse what the UI offers.
 */
export const ATTACHMENT_WRITE_KEY: Record<AttachmentScope, ResellerPermission> = {
  customer: "customers:edit",
  prospect: "prospects:view",
};

export function canManageFiles(
  permissions: readonly string[],
  scope: AttachmentScope,
): boolean {
  return permissions.includes(ATTACHMENT_WRITE_KEY[scope]);
}

/** The hook the ported panel calls. One line, so the panel's own edit stays
 * a call rather than a rule. */
export function useCanManageFiles(scope: AttachmentScope): boolean {
  const { permissions } = useAuth();
  return canManageFiles(permissions, scope);
}
